import React, { useState, useEffect } from 'react';
import { Globe, Shield, Mail, RefreshCw, CheckCircle, AlertCircle, ChevronRight, Save, Zap } from 'lucide-react';

const DomainManagement = () => {
    const [domain, setDomain] = useState('');
    const [transferStatus, setTransferStatus] = useState<{
        status: string;
        provider: string;
        lastUpdated: string;
    } | null>(null);
    const [dnsRecords, setDnsRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchStatus = async () => {
        if (!domain) {return;}
        setLoading(true);
        try {
            const res = await fetch(`/api/domains/status/${domain}`);
            const data = await res.json();
            setTransferStatus(data);
        } catch (e) {
            console.error("Error fetching status", e);
        } finally {
            setLoading(false);
        }
    };

    const startTransfer = async (provider: string) => {
        setLoading(true);
        try {
            await fetch('/api/domains/transfer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ domain, provider })
            });
            fetchStatus();
        } catch (e) {
            console.error("Transfer failed", e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-8 space-y-8 max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex items-center gap-4 mb-8">
                <div className="bg-blue-600 p-3 rounded-2xl text-white">
                    <Globe size={24} />
                </div>
                <div>
                    <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Domain Management</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Infrastructure & DNS Control</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-4">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Target Domain</label>
                        <div className="flex gap-2">
                            <input
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                className="flex-1 px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none"
                                placeholder="example.com"
                            />
                            <button onClick={fetchStatus} className="p-2 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
                                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-4">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-tight">Initiate Transfer</h3>
                        <div className="grid grid-cols-1 gap-2">
                            <button
                                onClick={() => startTransfer('cloudflare')}
                                className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                            >
                                <span className="text-sm font-bold text-slate-700">Cloudflare</span>
                                <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-500" />
                            </button>
                            <button
                                onClick={() => startTransfer('absolute_hosting')}
                                className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                            >
                                <span className="text-sm font-bold text-slate-700">Absolute Hosting</span>
                                <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-500" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-tight mb-4 flex items-center gap-2">
                            <Shield size={16} className="text-blue-500" />
                            Transfer Status
                        </h3>
                        {transferStatus ? (
                            <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                                <div className={`p-2 rounded-full ${transferStatus.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                                    {transferStatus.status === 'completed' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-slate-800 uppercase">{transferStatus.status}</p>
                                    <p className="text-xs text-slate-500">{transferStatus.provider} • {transferStatus.lastUpdated}</p>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-slate-400 italic">Enter a domain to track transfer progress</p>
                        )}
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
                                <Mail size={16} className="text-blue-500" />
                                Email Configuration
                            </h3>
                            <button
                                onClick={() => alert('Automatic Email DNS triggered')}
                                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full uppercase tracking-widest hover:bg-blue-700 transition-colors"
                            >
                                <Zap size={12} />
                                Auto-Setup
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-2xl border border-slate-100 bg-slate-50/50">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">MX Records</p>
                                <div className="text-xs font-medium text-slate-700 truncate">mail.example.com (10)</div>
                            </div>
                            <div className="p-4 rounded-2xl border border-slate-100 bg-slate-50/50">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">SPF Record</p>
                                <div className="text-xs font-medium text-slate-700 truncate">v=spf1 include:_spf.google.com ~all</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DomainManagement;
